<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import ResearchTreeNode from '@/components/research/ResearchTreeNode.vue';
import ResearchTreeEdges from '@/components/research/ResearchTreeEdges.vue';
import { useResearchTreeEdges, type TreeLink } from '@/composables/useResearchTreeEdges';
import type { ResearchNodeView } from '@/types/research';
import { levelsOfTier, firstLevelOfTier, lastLevelOfTier } from '@/utils/researchTier';

/**
 * Arbre de recherche d'une catégorie. Le NIVEAU de bâtiment requis pilote le
 * placement ; le PALIER (déduit, triangulaire) sert de bande de regroupement.
 * Deux niveaux de ZOOM :
 *   − (tier)  : une colonne par palier, nœuds rectangulaires empilés (vue d'ensemble).
 *   + (niveau): dans chaque bande de palier, une sous-colonne par niveau ; nœuds carrés.
 * Les liaisons SVG (prérequis → recherche débloquée) sont partagées avec l'éditeur
 * admin (useResearchTreeEdges + ResearchTreeEdges). Clic sur un nœud = sélection.
 */
const props = defineProps<{
  researches: ResearchNodeView[];
  selectedId?: number | null;
  /** % de progression par ResearchDefinitionId pour les recherches en cours. */
  progressById?: Record<number, number>;
  /** Niveau du Laboratoire central de la planète (palier max atteignable). */
  laboCentralLevel?: number | null;
  /** Niveau du Centre de recherche de la planète (labo de base). */
  centreLevel?: number | null;
}>();
defineEmits<{ select: [node: ResearchNodeView] }>();

const canvasRef = ref<HTMLElement | null>(null);
const zoom = ref<'tier' | 'level'>('tier');

// --- Regroupement par palier (bande) puis par niveau (sous-colonne) ----------
const byTier = computed(() => {
  const m = new Map<number, ResearchNodeView[]>();
  for (const n of props.researches) {
    const t = Math.max(0, n.tier ?? 0);
    (m.get(t) ?? m.set(t, []).get(t)!).push(n);
  }
  return m;
});
const tiers = computed(() => [...byTier.value.keys()].sort((a, b) => a - b));
const nodeById = computed(() => {
  const m = new Map<number, ResearchNodeView>();
  for (const n of props.researches) m.set(n.id, n);
  return m;
});

const sortNodes = (a: ResearchNodeView, b: ResearchNodeView) =>
  (a.order - b.order) || a.name.localeCompare(b.name);

function tierNodes(tier: number): ResearchNodeView[] {
  return [...(byTier.value.get(tier) ?? [])].sort(sortNodes);
}
/** Niveaux à afficher pour une bande de palier (au moins ceux réellement occupés). */
function tierLevels(tier: number): number[] {
  if (tier <= 0) return [0];
  return levelsOfTier(tier);
}
function nodesAtLevel(tier: number, level: number): ResearchNodeView[] {
  return tierNodes(tier).filter((n) => Math.max(0, n.requiredBuildingLevel ?? 1) === level);
}
function tierLabel(tier: number): string {
  if (tier <= 0) return 'Entrée';
  const lo = firstLevelOfTier(tier);
  const hi = lastLevelOfTier(tier);
  return lo === hi ? `Palier ${tier}` : `Palier ${tier}`;
}
function tierLevelRange(tier: number): string {
  if (tier <= 0) return 'niv. libre';
  const lo = firstLevelOfTier(tier);
  const hi = lastLevelOfTier(tier);
  return lo === hi ? `niv. ${lo}` : `niv. ${lo}–${hi}`;
}

// --- Liaisons (géométrie + rendu partagés) -----------------------------------
type EdgeVariant = 'met' | 'unmet' | 'selected';
// Colonne logique d'un nœud selon le zoom : palier (tier) ou niveau requis. Sert
// à orienter les courbes (même colonne = vertical, sinon gauche→droite).
const columnOf = (id: number): number => {
  const n = nodeById.value.get(id);
  if (!n) return 0;
  return zoom.value === 'tier' ? Math.max(0, n.tier ?? 0) : Math.max(0, n.requiredBuildingLevel ?? 0);
};

function buildLinks(): TreeLink<{ variant: EdgeVariant }>[] {
  const out: TreeLink<{ variant: EdgeVariant }>[] = [];
  for (const node of props.researches) {
    for (const p of node.prerequisites) {
      const variant: EdgeVariant = p.satisfied
        ? 'met'
        : (props.selectedId === node.id ? 'selected' : 'unmet');
      out.push({
        key: `${p.requiredId}-${node.id}`,
        sourceId: p.requiredId,
        targetId: node.id,
        data: { variant },
      });
    }
  }
  return out;
}

const { edges, svgW, svgH, scheduleRecompute } = useResearchTreeEdges(
  canvasRef, buildLinks, columnOf);

const renderEdges = computed(() => edges.value.map((e) => ({
  key: e.key, d: e.d, variant: e.data.variant,
})));

watch(() => props.researches, scheduleRecompute, { deep: true });
watch(() => props.selectedId, scheduleRecompute);
watch(zoom, scheduleRecompute);
</script>

<template>
  <div class="rt-wrap">
    <div class="rt-toolbar">
      <el-radio-group v-model="zoom" size="small">
        <el-radio-button label="tier">Zoom − (paliers)</el-radio-button>
        <el-radio-button label="level">Zoom + (niveaux)</el-radio-button>
      </el-radio-group>
    </div>

    <div class="rt-scroll">
      <div ref="canvasRef" class="rt-canvas">
        <ResearchTreeEdges :edges="renderEdges" :width="svgW" :height="svgH" />
        <div class="rt-bands">
          <div v-for="tier in tiers" :key="tier" class="rt-band">
            <div class="rt-band-head">
              <span class="rt-band-title">{{ tierLabel(tier) }}</span>
              <span class="rt-band-range">{{ tierLevelRange(tier) }}</span>
            </div>

            <!-- Zoom − : une colonne par palier, nœuds rectangulaires. -->
            <div v-if="zoom === 'tier'" class="rt-col">
              <ResearchTreeNode
                v-for="node in tierNodes(tier)"
                :key="node.id"
                :node="node"
                :selected="selectedId === node.id"
                :progress="progressById?.[node.id]"
                :labo-central-level="laboCentralLevel"
                :centre-level="centreLevel"
                @select="$emit('select', $event)"
              />
            </div>

            <!-- Zoom + : sous-colonnes par niveau, nœuds carrés. -->
            <div v-else class="rt-levels">
              <div v-for="lvl in tierLevels(tier)" :key="lvl" class="rt-levelcol">
                <div class="rt-level-head">{{ tier <= 0 ? 'libre' : `niv. ${lvl}` }}</div>
                <ResearchTreeNode
                  v-for="node in nodesAtLevel(tier, lvl)"
                  :key="node.id"
                  :node="node"
                  square
                  :selected="selectedId === node.id"
                  :progress="progressById?.[node.id]"
                  :labo-central-level="laboCentralLevel"
                :centre-level="centreLevel"
                  @select="$emit('select', $event)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <el-empty v-if="researches.length === 0" description="Aucune recherche." :image-size="56" />
  </div>
</template>

<style lang="scss" scoped>
// min-width:0 + max-width:100% : le wrap ne doit JAMAIS imposer sa largeur
// intrinsèque (l'arbre déroulé) à ses parents -> sinon la page déborde et une 2e
// scrollbar horizontale apparaît. Seul .rt-scroll défile.
.rt-wrap { display: flex; flex-direction: column; gap: $space-2; min-width: 0; max-width: 100%; }
// Barre d'outils (zoom) à GAUCHE : un flex-end la poussait hors écran quand
// l'arbre élargissait son conteneur. Alignée à gauche + hors de .rt-scroll ->
// toujours visible.
.rt-toolbar { display: flex; justify-content: flex-start; }
.rt-scroll { overflow-x: auto; max-width: 100%; padding-bottom: $space-2; }
.rt-canvas { position: relative; width: fit-content; min-width: 100%; }

// Bandes de palier alignées horizontalement ; large écart pour les courbes SVG.
.rt-bands {
  position: relative;
  z-index: 1; // au-dessus de la couche SVG des liaisons
  display: flex;
  gap: $space-12;
  align-items: flex-start;
}
.rt-band {
  display: flex;
  flex-direction: column;
  gap: $space-2;
  // Cadre léger de bande pour distinguer le regroupement par palier.
  border: 1px dashed rgba(255, 255, 255, 0.06);
  border-radius: $radius-md;
  padding: $space-2;
}
.rt-band-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding-bottom: $space-1;
}
.rt-band-title {
  font-size: 11px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--el-text-color-secondary);
}
.rt-band-range { font-size: $font-size-xs; opacity: 0.55; }

.rt-col { display: flex; flex-direction: column; gap: $space-3; flex: 0 0 auto; }

// Zoom niveau : sous-colonnes côte à côte dans la bande.
.rt-levels { display: flex; gap: $space-4; align-items: flex-start; }
.rt-levelcol { display: flex; flex-direction: column; gap: $space-2; flex: 0 0 auto; min-width: 96px; }
.rt-level-head {
  font-size: 10px; text-align: center; opacity: 0.5; text-transform: uppercase;
  letter-spacing: 0.03em;
}
</style>
